import { Link } from "react-router-dom";
import { MapPin, Heart, Zap, Coffee, BookOpen, Users } from "lucide-react";

const perks = [
  { icon: Heart, title: "Health & wellness", text: "Comprehensive health, dental, and vision coverage for you and your family." },
  { icon: Zap, title: "Latest hardware", text: "MacBook Pro, 4K monitor, and any tools you need — no questions asked." },
  { icon: Coffee, title: "Flexible work", text: "Fully remote-first. Work from anywhere in the world." },
  { icon: BookOpen, title: "Learning budget", text: "$2,000/year for courses, books, conferences — whatever helps you grow." },
  { icon: Users, title: "Team retreats", text: "Quarterly offsites to connect, collaborate, and recharge together." },
  { icon: MapPin, title: "Home office stipend", text: "$1,500 one-time setup bonus for your home workspace." },
];

const openings = [
  { title: "Senior Frontend Engineer", team: "Engineering", location: "Remote", type: "Full-time" },
  { title: "Backend Engineer (Rust/Go)", team: "Engineering", location: "Remote", type: "Full-time" },
  { title: "DevOps / Platform Engineer", team: "Infrastructure", location: "Remote", type: "Full-time" },
  { title: "Product Designer", team: "Design", location: "Remote", type: "Full-time" },
  { title: "Developer Advocate", team: "Community", location: "Remote", type: "Full-time" },
  { title: "Technical Writer", team: "Docs", location: "Remote", type: "Full-time / Contract" },
];

const CareersPage = () => {
  return (
    <div className="min-h-screen bg-white" data-testid="careers-page">
      {/* Hero */}
      <div className="bg-[#f8f9fa] border-b border-[#e8eaed]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-8 py-16 sm:py-20 lg:py-24">
          <div className="max-w-3xl">
            <p className="text-[12px] font-bold text-[#6C5CE7] tracking-wide uppercase mb-4">Careers</p>
            <h1 className="text-[32px] sm:text-[40px] lg:text-[48px] font-normal text-[#202124] leading-tight" data-testid="careers-title">
              Help us build the future of cloud computing
            </h1>
            <p className="mt-6 text-base sm:text-lg text-[#5f6368] leading-relaxed">
              We're a small, ambitious team building something big. Join us and work on problems that matter — with people who care about craft.
            </p>
          </div>
        </div>
      </div>

      {/* Perks */}
      <div className="max-w-[1440px] mx-auto px-6 lg:px-8 py-16 sm:py-20 lg:py-24">
        <h2 className="text-[28px] sm:text-[34px] font-normal text-[#202124] mb-12">Why Helocore</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="careers-perks">
          {perks.map((p, i) => {
            const Icon = p.icon;
            return (
              <div key={i} className="p-6 rounded-xl border border-[#e8eaed] hover:border-[#dadce0] hover:shadow-sm transition-all" data-testid={`perk-${i}`}>
                <div className="w-10 h-10 rounded-lg bg-[#fff4eb] flex items-center justify-center mb-4">
                  <Icon className="w-5 h-5 text-[#6C5CE7]" />
                </div>
                <h3 className="text-[15px] font-medium text-[#202124] mb-1.5">{p.title}</h3>
                <p className="text-[13px] text-[#5f6368] leading-relaxed">{p.text}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Open roles */}
      <div className="bg-[#f8f9fa] border-t border-[#e8eaed]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-8 py-16 sm:py-20 lg:py-24">
          <h2 className="text-[28px] sm:text-[34px] font-normal text-[#202124] mb-4">Open positions</h2>
          <p className="text-[15px] text-[#5f6368] mb-10">{openings.length} roles open across the company.</p>

          <div className="space-y-3" data-testid="careers-openings">
            {openings.map((job, i) => (
              <Link
                key={i}
                to="#"
                className="group flex flex-col sm:flex-row sm:items-center justify-between p-5 sm:p-6 bg-white rounded-xl border border-[#e8eaed] hover:border-[#dadce0] hover:shadow-sm transition-all"
                data-testid={`job-${i}`}
              >
                <div>
                  <h3 className="text-[15px] font-medium text-[#202124] group-hover:text-[#6C5CE7] transition-colors">{job.title}</h3>
                  <p className="text-[13px] text-[#5f6368] mt-1">{job.team}</p>
                </div>
                <div className="flex items-center gap-4 mt-3 sm:mt-0">
                  <span className="text-[12px] text-[#5f6368] bg-[#f1f3f4] px-3 py-1 rounded-full">{job.location}</span>
                  <span className="text-[12px] text-[#5f6368] bg-[#f1f3f4] px-3 py-1 rounded-full">{job.type}</span>
                  <svg className="w-4 h-4 text-[#9aa0a6] group-hover:text-[#6C5CE7] transition-colors" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CareersPage;
